<template>
  <FormSearchInput
    :error="customError"
    :isEmpty="!addressPredictions.length"
    :isListButtonShown="isListButtonShown"
    :isLoading="isBusy"
    :label="label"
    :value="searchTerm"
    hasInlineResults
    hasNoResultsMessage="No addresses were found for your search."
    id="address-search"
    listButtonText="Add Address Manually"
    placeholderText="Search an address..."
    @update:modelValue="handleAddressSearch"
    @click="handleListButtonClick"
  >
    <Stack as="ul" itemAs="li" :showDividers="true" space="none">
      <ListItem
        v-for="searchResult in addressPredictions"
        iconName="mapMarker"
        :isActionable="true"
        :key="searchResult.place_id"
        :title="searchResult.unparsed_address_part_one"
        :subtitle="searchResult.unparsed_address_part_two"
        :value="searchResult"
        @click="handleAddressItemClick(searchResult)"
      />
    </Stack>
  </FormSearchInput>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import { FormSearchInput, ListItem, Stack } from '@aryeo/runway'

import AddressSearchApi from '@/Services/Api/Company/V1/AddressSearchApi'

const props = defineProps({
  clearOnSelection: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: Object,
    default: null,
  },
  defaultAddress: {
    type: String,
    default: null,
  },
  isListButtonShown: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: 'Search Address',
  },
})

const emit = defineEmits(['onChange', 'onListButtonClick'])

const searchTerm = ref(props.defaultAddress)
const isBusy = ref(false)
const addressPredictions = ref([])
const coords = ref({
  latitude: null,
  longitude: null,
})

const customError = computed(() => {
  if (
    props.errors?.unparsed_address &&
    props.errors?.unparsed_address?.[0]?.includes(
      'The unparsed address field is required when none of latitude / longitude are present.'
    )
  ) {
    return 'Please enter a valid address.'
  }

  return props.errors?.unparsed_address?.[0]
})

onMounted(() => {
  getLocation()
})

const getLocation = () => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(showPosition)
  }
}

const showPosition = (position) => {
  coords.value.latitude = position.coords.latitude
  coords.value.longitude = position.coords.longitude
}

const handleAddressSearch = ({ value }) => {
  searchTerm.value = value

  if (searchTerm.value.length === 0) {
    addressPredictions.value = []
    return
  }

  isBusy.value = true

  AddressSearchApi.index(searchTerm.value, coords.value.latitude, coords.value.longitude)
    .then(({ data }) => (addressPredictions.value = data.data))
    .finally(() => (isBusy.value = false))
}

const handleAddressItemClick = (addressPrediction) => {
  if (props.clearOnSelection) {
    searchTerm.value = ''
  } else {
    searchTerm.value = addressPrediction.unparsed_address
  }

  emitChange(addressPrediction)
}

const emitChange = (address) => {
  if (address) {
    emit('onChange', address)
    return
  }

  emit('onChange', null)
}

const handleListButtonClick = () => {
  emit('onListButtonClick')
}
</script>
